import { Buffer } from "buffer";
import axios from "axios";
import { readStoredAuthToken } from "../auth/authApi";
import { BACKEND_URL, getLegacyAuthBaseUrl } from "../config";
import pb from "./pocketbase";

const APP_URL = BACKEND_URL;

function bearerHeaders() {
  const t = readStoredAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Dev: same-origin `/profile/...` → setupProxy → API host (CORS fix for browser).
 * Prod: full BACKEND_URL.
 */
function profileApiRoot() {
  if (process.env.NODE_ENV === "development") {
    return "";
  }
  return String(BACKEND_URL || "").replace(/\/$/, "");
}

export const getLikedItems = async () => {
  const res = await axios.get(`${APP_URL}/profile/my-favs`, {
    headers: bearerHeaders(),
  });

  return res.data || { myTools: [], myResos: [], myIdeaz: [] };
};

export const getProfile = async (id) => {
  const res = await axios.get(`${APP_URL}/profile/${id}`, {
    headers: bearerHeaders(),
  });

  return res;
};

export const getImageProfile = async (image_name) => {
  const res = await axios.get(`${BACKEND_URL}/profile/image/${image_name}`, {
    responseType: "arraybuffer",
    responseEncoding: "base64",
  });
  const buffer = Buffer.from(res.data, "base64");
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

// toggle hide/show profile Uxer
export const toggleVisibilityUxer = (value) => {
  if (value) {
    axios.post(`${BACKEND_URL}/uxers/hide-my-profile`, {}, { headers: bearerHeaders() });
  } else {
    axios.post(`${BACKEND_URL}/uxers/show-my-profile`, {}, { headers: bearerHeaders() });
  }
};

// change email (legacy API; dev uses same-origin + proxy — see profileApiRoot)
export const changeEmail = async (new_email, password, lang) => {
  const res = await axios.post(
    `${profileApiRoot()}/profile/change-email`,
    {
      newEmail: new_email,
      password: password,
      lang,
    },
    {
      headers: bearerHeaders(),
    }
  );

  return res;
};

// change password
export const changePassword = async (last_password, new_password) => {
  const res = axios.post(
    `${BACKEND_URL}/profile/changePassword`,
    {
      oldPassword: last_password,
      newPassword: new_password,
      repeatedNewPassword: new_password,
    },
    {
      headers: bearerHeaders(),
    }
  );

  return res;
};

// change notification
export const changeNotification = async (index) => {
  if (index === 0) {
    axios.post(`${BACKEND_URL}/profile/allow-send-email`, {}, { headers: bearerHeaders() });
  } else {
    axios.post(`${BACKEND_URL}/profile/receive-news`, {}, { headers: bearerHeaders() });
  }
};

// change profile info
export const changeProfileInfo = async (data) => {
  const formData = new FormData();

  const picRes = await axios.get(data.profile_img, {
    responseType: "blob",
  });

  const blob = picRes.data;

  const mimeType = blob.type;
  const fileName = `profile_image.${mimeType.split("/")[1]}`;
  const file = new File([blob], fileName, { type: mimeType });

  formData.append("profile", file);
  formData.append("email", data.email);
  formData.append("password", data.password);
  formData.append("username", data.username);
  formData.append("fullName", data.fullName);
  formData.append("nationality", data.nationality);
  formData.append("sub_role", data.subRole);
  formData.append("intro", data.intro);
  formData.append("personalSite", data.personalSite);
  formData.append("personalBlog", data.personalBlog);
  formData.append("twitterLink", data.twitterLink);
  formData.append("facebookLink", data.facebookLink);
  formData.append("instagramLink", data.instagramLink);
  formData.append("linkedinLink", data.linkedinLink);
  formData.append("dribbleLink", data.dribbleLink);
  formData.append("behanceLink", data.behanceLink);
  formData.append("experience", data.experience);
  formData.append("position", data.position);
  formData.append("skills", data.skills);

  const res = await axios.post(`${APP_URL}/profile/`, formData, {
    headers: bearerHeaders(),
  });

  if (res.data?.messageSuccess) {
    return "success";
  } else {
    return res.data;
  }
};

export const submitRequestUxer = async (userData) => {
  const res = await axios.post(
    `${APP_URL}/uxers/uxers-request`,
    {
      skills: userData.skills ? userData.skills?.join(",") : "",
      position: userData.position,
      experience: userData.experience ? parseInt(userData.experience) : 0,
      spokenLangs: "English",
    },
    {
      headers: bearerHeaders(),
    }
  );

  return res;
};

export const deleteMyUxer = async () => {
  const res = await axios.post(
    `${APP_URL}/uxers/delete-myaccount`,
    {},
    {
      headers: bearerHeaders(),
    }
  );

  return res;
};

export const createNewPassword = async (values, id) => {
  const res = await axios.post(
    `${APP_URL}/profile/create-new-password/${id}`,
    values
  );

  if (res) {
    localStorage.setItem("token", JSON.stringify(res.data.myToken));
    return res;
  }

  // .then((res) => {
  //     localStorage.setItem("token", JSON.stringify(res.data.myToken));
  //     console.log(res.data.myToken);
  //     return res;
  // })
  // .catch((error) => {
  //   throw error;
  // });
};

export const deleteAccount = async (lang) => {
  const bearer = readStoredAuthToken();
  if (!bearer) {
    const err = new Error("Not authenticated");
    err.response = { status: 401, data: { message: "Not authenticated" } };
    throw err;
  }

  // Primary: PocketBase auth — legacy `/profile/delete-account` often 404s for PB-only users.
  try {
    if (!pb.authStore.isValid || !pb.authStore.model?.id) {
      pb.authStore.save(bearer, null);
      await pb.collection("users").authRefresh();
    }
  } catch {
    // Not a PocketBase session or token invalid — fall back to legacy API below.
  }

  if (pb.authStore.isValid && pb.authStore.model?.id) {
    try {
      await pb.collection("users").delete(pb.authStore.model.id);
      pb.authStore.clear();
      localStorage.removeItem("token");
      return {
        data: {
          status: true,
          message:
            lang === "ja"
              ? "アカウントを削除しました。"
              : "Your account has been deleted.",
        },
      };
    } catch (e) {
      const status = e?.status ?? e?.response?.status ?? 0;
      const msg = e?.message || e?.data?.message || "Delete failed";
      if (status === 404) {
        pb.authStore.clear();
        localStorage.removeItem("token");
        return {
          data: {
            status: true,
            message:
              lang === "ja"
                ? "アカウントを削除しました。"
                : "Your account has been deleted.",
          },
        };
      }
      if (status === 403 || status === 400) {
        const err = new Error(msg);
        err.response = { status, data: { message: msg } };
        throw err;
      }
    }
  }

  const res = await axios.post(
    `${profileApiRoot()}/profile/delete-account`,
    { lang },
    {
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
    }
  );

  return res;
};

export const getSignupRequest = async (id) => {
  const base = getLegacyAuthBaseUrl();
  const res = await axios.get(`${base}/auth/signup_request/${id}`);
  return res;
};

/** Profile fallback; dev uses same-origin + proxy when PB rules block. */
export const getUserInfo = async (username) => {
  const res = await axios.get(
    `${profileApiRoot()}/profile/user/${username}`
  );

  return res;
};
export const likeItem = async (id_item, categorie) => {
  const t = readStoredAuthToken();
  let url = `${BACKEND_URL}/items/${id_item}`;

  if (categorie === "tools") {
    url += "/64761102ba22fed734b9de7a";
  } else if (categorie === "resos") {
    url += "/64761222ba22fed734b9de7d";
  } else if (categorie === "ideaz") {
    url += "/64761240ba22fed734b9de81";
  } else if (categorie === "apps") {
    url += "/65a2912599ed3cb8b9886940";
  }

  if (!t) return undefined;
  const res = await axios.post(url, {}, { headers: bearerHeaders() });
  return res;
};
export const getUserFavs = async (id) => {
  const res = await axios.get(`${BACKEND_URL}/profile/favs/${id}`);

  return res;
};

export const getUserDetails = async (userId) => {
  const res = await axios.get(`${BACKEND_URL}/profile/${userId}`, {
    headers: bearerHeaders(),
  });
  return res.data;
};
